import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Loader2, Lock } from "lucide-react";
import { login, setToken } from "../api";
import { useAdmin } from "./AdminContext";
import { T, INP, CLIENT_BRAND } from "./theme";

function formatErr(detail) {
  if (!detail) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function ClientLogo() {
  const [imgOk, setImgOk] = useState(true);
  if (imgOk) {
    return (
      <img
        src={CLIENT_BRAND.logo}
        alt={CLIENT_BRAND.name}
        onError={() => setImgOk(false)}
        className="h-12 w-auto"
        style={{ filter: "drop-shadow(0 2px 10px rgba(244,206,10,0.28))" }}
      />
    );
  }
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-flex items-center justify-center w-11 h-11 rounded-xl font-black text-xl"
        style={{ background: `linear-gradient(135deg, ${T.gold}, ${T.goldHi})`, color: "#0A0A0C" }}
      >
        T
      </span>
      <div className="leading-tight">
        <div className="font-display font-extrabold text-base tracking-tight" style={{ color: T.text }}>
          Top of the Line
        </div>
        <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: T.gold }}>
          Tree Services LLC
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { setUser } = useAdmin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const data = await login(email, password);
      if (data.user?.role === "super_admin") {
        // Super admins must use the agency portal, not the client portal
        setErr("This account is not authorized for the client portal.");
        return;
      }
      setToken(data.token);
      setUser(data.user);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 relative"
      style={{
        background: `radial-gradient(1200px 600px at 20% -10%, rgba(212,175,55,0.10), transparent 60%), radial-gradient(900px 500px at 90% 110%, rgba(212,175,55,0.06), transparent 55%), ${T.bg}`,
      }}
    >
      <Helmet>
        <title>Top of the Line Tree Services LLC - Client Portal</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative z-10 w-full max-w-md p-8 rounded-2xl backdrop-blur-sm"
        style={{ background: T.card, border: `1px solid ${T.border}`, boxShadow: T.shadow }}
      >
        <div className="mb-7 flex items-center justify-between">
          <ClientLogo />
          <span
            className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full"
            style={{ background: T.goldSoft, color: T.gold }}
          >
            <Lock size={10} /> Secure
          </span>
        </div>

        <h1 className="font-display font-extrabold text-2xl mb-1 leading-tight" style={{ color: T.text }}>
          Welcome to Top of the Line Tree Services LLC
        </h1>
        <p className="text-sm mb-6" style={{ color: T.textDim }}>
          Sign in to manage your company website.
        </p>

        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: T.textDim }}>
          Email
        </label>
        <input
          data-testid="admin-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full mb-4 px-4 py-3 text-sm outline-none focus:ring-2 rounded-lg"
          style={{ ...INP, boxShadow: "none" }}
        />

        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: T.textDim }}>
          Password
        </label>
        <input
          data-testid="admin-password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full mb-5 px-4 py-3 text-sm outline-none focus:ring-2 rounded-lg"
          style={INP}
        />

        {err && (
          <p
            data-testid="admin-login-error"
            className="mb-4 text-sm px-3 py-2 rounded-lg"
            style={{ background: "rgba(239,68,68,0.12)", color: "#fca5a5" }}
          >
            {err}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full rounded-lg py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-transform active:scale-[0.98]"
          style={{ background: `linear-gradient(135deg, ${T.gold}, ${T.goldHi})`, color: "#0A0A0C" }}
        >
          {loading && <Loader2 size={18} className="animate-spin" />} Sign in
        </button>

        <p className="mt-6 text-[11px] text-center" style={{ color: T.textFaint }}>
          Protected by JWT auth · brute-force lockout · role-based access control
        </p>
      </form>
    </div>
  );
}
